import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Link,
  Skeleton,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";

import { sanitizeInput } from "features/Auth/Onboarding/AboutForm/helpers";
import { fetcherAuth } from "utils/api";

import InstagramSetup from "../InstagramSetup";
import styles from "../styles";
import Collection from "./Collection";
import InstagramEditDialog from "./InstagramEditDialog";
import { InstagramMentionCollection } from "./schema";

interface InstagramConnectedProps {
  closeDialog?: () => void;
}

export default function InstagramConnected({
  closeDialog,
}: InstagramConnectedProps) {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const { profile, setProfile, currentOrg } = useContext(
    OrganizationUserContext,
  );

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mentions, setMentions] = useState<InstagramMentionCollection[]>([]);
  const [selectedInstagramMention, setSelectedInstagramMention] =
    useState<InstagramMentionCollection | null>(null);
  const { setUpgradeDialogSource, subscription } =
    useContext(SubscriptionContext);

  const currentUsername = sanitizeInput(profile?.instagramUrl || "");

  const fetchInstagramMentionCollection = async () => {
    if (!currentOrg?.id) return;

    setLoading(true);

    try {
      const { mentions } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/saved-brand-collections/instagram-collection`,
        "GET",
      );
      setMentions(mentions);
    } catch (error) {
      setAlert(error.message || "Failed to fetch instagram mentions", "error");
    } finally {
      setLoading(false);
    }
  };

  const saveInstagramProfile = async (handle: string) => {
    if (!handle || !currentOrg?.id) return;

    try {
      const { organizationProfile } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/onboarding`,
        "PUT",
        {},
        {
          instagramUrl: `https://instagram.com/${handle}`,
          page: "Instagram Sidebar",
        },
      );

      setProfile((prev) => ({
        ...prev,
        ...organizationProfile,
      }));

      setAlert("Instagram account updated successfully!", "success");
    } catch (error: any) {
      setAlert(error.message || "Failed to update Instagram account", "error");
    }
  };

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  useEffect(() => {
    fetchInstagramMentionCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return (
    <>
      <Box>
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={styles.header}
        >
          <Grid container item xs={9} alignItems="center">
            <Typography variant="h6" component="div" sx={{ fontSize: 14 }}>
              INSTAGRAM
            </Typography>
          </Grid>

          <Grid container item xs justifyContent="flex-end">
            <Box
              component="button"
              onClick={closeDialog}
              sx={{
                ...styles.close,
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 1,
              }}
            >
              <Box component="i" className="fa-regular fa-xmark" />
            </Box>
          </Grid>
        </Grid>

        <Box sx={styles.content}>
          <Box sx={styles.textSection}>
            <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 1 }}>
              Creators who pitch using this feature see a{" "}
              <Box
                component="span"
                sx={{ color: "green.main", fontWeight: 600 }}
              >
                42% higher response rate
              </Box>
              .
            </Typography>

            {profile?.instagramUrl ? (
              <Box sx={styles.accountSection}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Connected Instagram Account
                </Typography>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item xs>
                    <Box sx={styles.usernameDisplay}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        @{currentUsername}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs="auto">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleEditClick}
                      sx={styles.editButton}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-edit"
                        sx={{ mr: 1 }}
                      />
                      Edit
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <InstagramSetup closeDialog={closeDialog} />
            )}

            <Box>
              <Typography variant="h6" sx={{ my: 1 }}>
                How It Works
              </Typography>
              <Link
                sx={{
                  fontWeight: 600,
                  color: "purple.main",
                  textDecoration: "none",
                  borderBottom: "1px solid",
                  borderColor: "purple.main",
                }}
                href="https://www.loom.com/share/ec89eca235e34f38b68db2e2de3935b3"
                target="_blank"
              >
                Watch Tutorial
              </Link>
              <ol>
                <li>
                  Tag{" "}
                  <Box
                    component="a"
                    href="https://instagram.com/onbento"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={styles.link}
                  >
                    @onbento
                  </Box>{" "}
                  in a creator's post
                </li>
                <li>
                  We'll add the brands they've tagged to your list so you can
                  pitch them
                </li>
                <li>
                  If you use our Virtual Assistant, those brands will also be
                  added to next week's upcoming pitches automatically.
                </li>
              </ol>
              📌{" "}
              {subscription?.isSubscribed
                ? "Because you're on a paid plan,"
                : "If you're on a paid plan,"}{" "}
              we won't respond so you can tag under the radar.
            </Box>
            {!subscription?.isSubscribed && (
              <Button
                sx={{ mt: 1 }}
                variant="contained"
                disableElevation
                size="small"
                onClick={() =>
                  setUpgradeDialogSource("Instagram Connected Sidebar")
                }
              >
                Upgrade Now
              </Button>
            )}
          </Box>

          <Box sx={styles.brandsSection}>
            <Typography variant="h6" sx={{ my: 1, ml: 1 }}>
              Saved Lists
            </Typography>
            {loading ? (
              <Box>
                {Array.from({ length: 3 }).map((_, index) => (
                  <Grid key={index} container sx={{ p: 2 }} gap={1}>
                    <Grid item xs={2}>
                      <Skeleton variant="rounded" width={50} height={30} />
                    </Grid>
                    <Grid container item xs gap={1}>
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={20}
                        width="100%"
                      />
                      <Skeleton
                        animation="wave"
                        variant="rounded"
                        height={20}
                        width="60%"
                      />
                    </Grid>
                  </Grid>
                ))}
              </Box>
            ) : (
              <>
                {selectedInstagramMention && (
                  <>
                    <Button
                      size="small"
                      onClick={() => {
                        setSelectedInstagramMention(null);
                      }}
                      color="secondary"
                      sx={{ textTransform: "none", pl: 1 }}
                    >
                      <Box
                        component="i"
                        className="fa-regular fa-arrow-left"
                        sx={{ mr: 1 }}
                      />
                      Back{" "}
                    </Button>
                    <Collection
                      selectedInstagramMention={selectedInstagramMention}
                    />
                  </>
                )}
                {!selectedInstagramMention &&
                  mentions.map((mention) => (
                    <Grid
                      key={mention.sourceInstagramUsername}
                      container
                      alignItems="center"
                      sx={{
                        cursor: "pointer",
                        py: 2,
                        px: 1,
                        borderBottom: `1px solid ${grey[200]}`,
                      }}
                      onClick={() => {
                        setSelectedInstagramMention(mention);
                      }}
                      justifyContent="space-between"
                    >
                      <Box>
                        <Typography>
                          @{mention.sourceInstagramUsername}
                        </Typography>
                        <Typography variant="body2" sx={{ color: grey[600] }}>
                          {mention.unsentCount === 0 &&
                          mention.sentCount === 0 &&
                          mention.status === "pending"
                            ? "Pending"
                            : `Unsent (${mention.unsentCount}) | Sent (${mention.sentCount})`}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton
                          sx={{
                            fontSize: 12,
                            color: "black",
                            width: 25,
                            height: 25,
                          }}
                        >
                          <Box
                            component="i"
                            className="fa-regular fa-arrow-right"
                          />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                {!selectedInstagramMention &&
                  mentions.length === 0 &&
                  !loading && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontStyle: "italic", px: 2 }}
                    >
                      No brands here yet. Tag @onbento on Instagram posts to add
                      brands automatically!
                    </Typography>
                  )}
              </>
            )}
          </Box>
        </Box>
      </Box>

      <InstagramEditDialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        currentUsername={currentUsername}
        onSave={saveInstagramProfile}
      />
    </>
  );
}
